import { usePersistedState } from "@/lib/persistedFilters";
// Rate card tab — products × commission levels grid (standard / contracted %).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RefreshCw, Search, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { apiError, money, thCls } from "./engineShared";

const cellKey = (pid, lid) => `${pid}:${lid}`;

export function EngineRateCard({ canManage, reloadOverview }) {
  const [levels, setLevels] = useState([]);
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({});
  const [q, setQ] = usePersistedState("engine-rates.q", "");
  const [showRetired, setShowRetired] = useState(false);
  const [onlyRated, setOnlyRated] = usePersistedState("engine-rates.only-rated", false);
  const [typeFilter, setTypeFilter] = usePersistedState("engine-rates.type", "all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newLevel, setNewLevel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/engine/ratecard", { params: { include_retired: showRetired ? 1 : 0 } });
      setLevels(data.levels || []); setProducts(data.products || []); setEdits({});
    } catch (e) { apiError(e, "Could not load the rate card"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showRetired]);

  const productTypes = useMemo(
    () => Array.from(new Set(products.map((p) => p.product_type).filter(Boolean))).sort(),
    [products]);

  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    return products.filter((p) => {
      if (onlyRated && Object.keys(p.rates || {}).length === 0) return false;
      if (typeFilter !== "all" && (p.product_type || "") !== typeFilter) return false;
      if (!t) return true;
      return (p.name || "").toLowerCase().includes(t) || (p.code || "").toLowerCase().includes(t) || String(p.product_id).includes(t);
    });
  }, [products, q, onlyRated, typeFilter]);

  const val = (p, lid, field) => {
    const k = cellKey(p.product_id, lid);
    if (edits[k] && edits[k][field] !== undefined) return edits[k][field];
    const r = (p.rates || {})[String(lid)];
    return r ? r[field] : "";
  };
  const setVal = (p, lid, field, v) => {
    const k = cellKey(p.product_id, lid);
    setEdits((e) => ({ ...e, [k]: { ...(e[k] || {}), [field]: v } }));
  };

  const save = async () => {
    const rates = [];
    for (const [k, e] of Object.entries(edits)) {
      const [pid, lid] = k.split(":").map(Number);
      const p = products.find((x) => x.product_id === pid);
      const cur = (p?.rates || {})[String(lid)] || {};
      const std = e.standard_rate !== undefined ? e.standard_rate : cur.standard_rate;
      const con = e.contracted_rate !== undefined ? e.contracted_rate : cur.contracted_rate;
      if (std === "" && con === "") continue;
      rates.push({ product_id: pid, level_id: lid, standard_rate: Number(std || 0), contracted_rate: Number(con || 0) });
    }
    if (!rates.length) { toast.message("Nothing to save"); return; }
    setSaving(true);
    try { await api.put("/commissions/engine/ratecard", { rates }); toast.success(`Saved ${rates.length} rate cell${rates.length > 1 ? "s" : ""}`); await load(); reloadOverview?.(); }
    catch (e) { apiError(e, "Could not save"); } finally { setSaving(false); }
  };
  const clearCell = async (p, lid) => {
    try { await api.delete(`/commissions/engine/ratecard/${p.product_id}/${lid}`); toast.success("Rate removed"); load(); }
    catch (e) { apiError(e); }
  };
  const refreshProducts = async () => {
    try { const { data } = await api.post("/commissions/engine/products/refresh"); toast.success(`${data.products} products from the billing platform, ${data.new} new`); load(); }
    catch (e) { apiError(e, "Could not refresh products"); }
  };
  const addLevel = async () => {
    if (!newLevel.trim()) return;
    try { await api.post("/commissions/engine/levels", { name: newLevel.trim() }); toast.success("Level added"); setNewLevel(""); load(); }
    catch (e) { apiError(e); }
  };
  const removeLevel = async (l) => {
    if (!window.confirm(`Delete level "${l.name}" and all of its rates?`)) return;
    try { await api.delete(`/commissions/engine/levels/${l.level_id}`); toast.success("Level deleted"); load(); }
    catch (e) { apiError(e); }
  };

  const dirty = Object.keys(edits).length;
  return (
    <div className="space-y-3" data-testid="engine-ratecard">
      <Card className="border-slate-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a product" className="h-9 pl-8" data-testid="engine-rc-search" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={onlyRated} onCheckedChange={setOnlyRated} data-testid="engine-rc-onlyrated" /> Only products with rates</label>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Type</span>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 w-44" data-testid="engine-rc-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All types</SelectItem>
                {productTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={showRetired} onCheckedChange={setShowRetired} data-testid="engine-rc-retired" /> Show retired</label>
          <div className="ml-auto flex items-center gap-2">
            {canManage && (
              <div className="flex items-center gap-1">
                <Input value={newLevel} onChange={(e) => setNewLevel(e.target.value)} placeholder="New level" className="h-9 w-32" data-testid="engine-rc-newlevel" />
                <Button variant="outline" className="h-9" onClick={addLevel} disabled={!newLevel.trim()} data-testid="engine-rc-addlevel"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
            {canManage && <Button variant="outline" className="h-9" onClick={refreshProducts} data-testid="engine-rc-refresh"><RefreshCw className="h-4 w-4" /> Pull products from Billing Platform</Button>}
            {canManage && <Button className="h-9" onClick={save} disabled={!dirty || saving} data-testid="engine-rc-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save{dirty ? ` (${dirty})` : ""}</Button>}
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Each cell is <b>standard % / contracted %</b>. Standard applies to month-to-month service; contracted applies while the Billing Platform subscription is under contract (or a deal forces it). Leave a cell empty and the product earns nothing at that level — those lines show as “no rate” on the Commission Ledger tab.</p>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-rc-table">
            <thead className="bg-slate-50">
              <tr>
                <th className={thCls}>Product</th><th className={thCls}>Type</th><th className={`${thCls} text-right`}>Last 2 months</th>
                {levels.map((l) => (
                  <th key={l.level_id} className={`${thCls} text-center`} data-testid={`engine-rc-level-${l.level_id}`}>
                    <div className="flex items-center justify-center gap-1">{l.name}
                      {canManage && <button type="button" onClick={() => removeLevel(l)} className="text-slate-300 hover:text-rose-600" title="Delete level" data-testid={`engine-rc-dellevel-${l.level_id}`}><Trash2 className="h-3 w-3" /></button>}
                    </div>
                    <div className="text-[9px] font-normal normal-case text-slate-400">std / contracted</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={3 + levels.length} className="px-3 py-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && visible.length === 0 && <tr><td colSpan={3 + levels.length} className="px-3 py-8 text-center text-slate-400">No products</td></tr>}
              {!loading && visible.map((p) => (
                <tr key={p.product_id} className={`border-t border-slate-100 ${p.status === "Retired" ? "opacity-60" : ""}`} data-testid={`engine-rc-row-${p.product_id}`}>
                  <td className="px-3 py-1.5"><div className="font-medium text-slate-800">{p.name}</div><div className="text-[11px] text-slate-400">{p.code} · #{p.product_id}{p.status === "Retired" ? " · retired" : ""}</div></td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{p.product_type}</td>
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{p.recent?.lines ? `${p.recent.lines} lines · ${money(p.recent.billed)}` : "—"}</td>
                  {levels.map((l) => {
                    const has = !!(p.rates || {})[String(l.level_id)];
                    const k = cellKey(p.product_id, l.level_id);
                    return (
                      <td key={l.level_id} className={`px-2 py-1.5 ${edits[k] ? "bg-amber-50" : ""}`}>
                        <div className="flex items-center justify-center gap-1">
                          <Input type="number" step="0.01" min="0" max="100" disabled={!canManage} value={val(p, l.level_id, "standard_rate")}
                                 onChange={(e) => setVal(p, l.level_id, "standard_rate", e.target.value)} className="h-8 w-16 px-1 text-center text-xs" placeholder="—" data-testid={`engine-rc-std-${p.product_id}-${l.level_id}`} />
                          <span className="text-slate-300">/</span>
                          <Input type="number" step="0.01" min="0" max="100" disabled={!canManage} value={val(p, l.level_id, "contracted_rate")}
                                 onChange={(e) => setVal(p, l.level_id, "contracted_rate", e.target.value)} className="h-8 w-16 px-1 text-center text-xs" placeholder="—" data-testid={`engine-rc-con-${p.product_id}-${l.level_id}`} />
                          {canManage && has && <button type="button" onClick={() => clearCell(p, l.level_id)} className="text-slate-300 hover:text-rose-600" title="Remove rate" data-testid={`engine-rc-clear-${p.product_id}-${l.level_id}`}><Trash2 className="h-3 w-3" /></button>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
